import { Head, Link, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { AutomationsSubNav } from "@/components/automations/AutomationsSubNav"
import { FlashNotice } from "@/components/admin/FlashNotice"
import { Pagination } from "@/components/Pagination"
import { RunBadges } from "@/components/runs/RunBadges"
import { RunFilters } from "@/components/runs/RunFilters"
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table"
import type { PageProps } from "@/types/inertia"
import type {
  Pagination as PaginationMeta,
  RunFilterOptions,
  RunFilterValues,
  RunRow,
} from "@/types/run"

const DESCRIPTION =
  "Every time an automation fired: who it matched, who was texted, who was skipped, and why."

export default function RunsIndex({
  runs,
  filters,
  filter_options,
  pagination,
}: {
  runs: RunRow[]
  filters: RunFilterValues
  filter_options: RunFilterOptions
  pagination: PaginationMeta
}) {
  const { props } = usePage<PageProps>()
  const viewing = props.viewing
  const allOrgs = filters.organization === "all"

  return (
    <>
      <Head title="History">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="History" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <PageHeader
          title="Automations"
          description={
            allOrgs
              ? "Every run across every District Council."
              : `Every time an automation fired for ${viewing?.name ?? "your organization"}.`
          }
          tabs={<AutomationsSubNav />}
        />

        <div className="mt-6">
          <FlashNotice />

          <RunFilters filters={filters} options={filter_options} />

          {pagination.total_count === 0 ? (
            <div className="callout">
              <p className="text-sm text-ink-muted">
                No runs to show. A run is recorded every time an automation
                fires, so once one is switched on under Automations its first
                run will appear here.
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>When</TableHeaderCell>
                    <TableHeaderCell>Automation</TableHeaderCell>
                    {allOrgs && <TableHeaderCell>Organization</TableHeaderCell>}
                    <TableHeaderCell numeric>Matched</TableHeaderCell>
                    <TableHeaderCell numeric>Sent</TableHeaderCell>
                    <TableHeaderCell numeric>Failed</TableHeaderCell>
                    <TableHeaderCell numeric>Opted out</TableHeaderCell>
                    <TableHeaderCell numeric>No mobile</TableHeaderCell>
                    <TableHeaderCell>Result</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.length === 0 ? (
                    <TableEmpty colSpan={allOrgs ? 9 : 8}>
                      No runs match these filters.
                    </TableEmpty>
                  ) : (
                    runs.map((run) => (
                      <TableRow key={run.id}>
                        <TableCell nowrap>
                          <Link href={`/runs/${run.id}`} className="no-underline">
                            {run.started_at_label ?? "Not started"}
                          </Link>
                        </TableCell>
                        <TableCell nowrap>
                          {run.automation_name}
                          {/* Which of the automation's setups fired. Twenty
                              meeting reminders are otherwise twenty rows all
                              reading the same thing. */}
                          {run.setting_label && (
                            <span className="block text-ink-muted">
                              {run.setting_label}
                            </span>
                          )}
                        </TableCell>
                        {allOrgs && (
                          <TableCell nowrap>{run.organization_name}</TableCell>
                        )}
                        <TableCell numeric>
                          {run.matched_count.toLocaleString()}
                        </TableCell>
                        <TableCell numeric>
                          {run.sent_count.toLocaleString()}
                        </TableCell>
                        <TableCell
                          numeric
                          className={run.failed_count > 0 ? "text-danger-display" : undefined}
                        >
                          {run.failed_count.toLocaleString()}
                        </TableCell>
                        <TableCell numeric>
                          {run.opted_out_count.toLocaleString()}
                        </TableCell>
                        <TableCell numeric>
                          {run.no_mobile_count.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <span className="flex flex-wrap items-center gap-1.5">
                            <RunBadges run={run} />
                          </span>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <Pagination
                meta={pagination}
                path="/runs"
                label="runs"
                params={{
                  automation_id: filters.automation_id,
                  from: filters.from,
                  to: filters.to,
                  result: filters.result === "all" ? null : filters.result,
                  kind: filters.kind === "all" ? null : filters.kind,
                  organization: allOrgs ? "all" : null,
                }}
              />
            </>
          )}

          <p className="mt-4 text-xs text-ink-muted">
            Matched is always the sum of sent, failed, opted out, no mobile
            number, and already texted that day — open a run to see every
            member and the reason for each one.
          </p>
        </div>
      </AppShell>
    </>
  )
}
